import React, { useState } from 'react';
import { Wifi, WifiOff, Loader2 } from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import { LobbyHubNotificationIndicator, getPlayerLeague } from './lobbyHubShared';
import { LOBBY_HOME_ASSETS } from '../lib/lobbyHomeAssets';

const GUEST_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/guest-avatar.png`;
const FALLBACK_GUEST_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/avatar-for-bot.png`;

const strokeTextStyle = { WebkitTextStroke: '1.1px #000', paintOrder: 'stroke fill' };

function formatHubBalance(value) {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return n.toLocaleString('ru-RU');
}

/** Магазин / кошелёк: колонка, весь блок кликабелен. */
const HUB_RESOURCE_BTN =
  'lobby-focus relative z-[2] m-0 flex w-[4.25rem] min-w-0 shrink-0 flex-col items-center gap-0.5 border-0 bg-transparent p-0 shadow-none outline-none transition active:scale-[0.98] sm:w-[4.5rem]';

const HUB_RESOURCE_RING =
  'relative flex h-[52px] w-full items-center justify-center sm:h-[54px]';

const HUB_RESOURCE_DECOR =
  'pointer-events-none absolute inset-0 m-auto h-[52px] w-[56px] max-w-none object-contain sm:h-[54px] sm:w-[58px]';

const HUB_SHOP_BTN_IMG =
  'pointer-events-none relative z-[1] h-[38px] w-[40px] max-w-none object-contain select-none sm:h-[40px] sm:w-[42px]';

const HUB_WALLET_BTN_IMG =
  'pointer-events-none relative z-[1] h-[38px] w-[41px] max-w-none object-contain select-none sm:h-[40px] sm:w-[42px]';

const HUB_BALANCE_CAPTION =
  'mb-[3px] block w-full max-w-full min-w-0 truncate text-center text-[9px] font-bold tabular-nums leading-snug tracking-tight text-white/90 sm:text-[10px]';

const HUB_ROW2_BTN =
  'lobby-focus relative m-0 flex h-[67px] w-[67px] shrink-0 items-center justify-center overflow-visible border-0 bg-transparent p-0 shadow-none outline-none transition active:scale-[0.98] sm:h-[68px] sm:w-[68px]';

const HUB_ROW2_TILE_IMG =
  'pointer-events-none h-[min(100%,4.2rem)] w-[min(100%,4.2rem)] max-h-[68px] max-w-[68px] object-contain select-none';

/**
 * Хаб-шапка лобби: flex (ник в потоке), декоративные SVG через absolute внутри shell.
 */
export function LobbyHubHeader({
  user,
  connectionStatus,
  onProfileClick,
  onOpenShop,
  onOpenWallet,
  onDailyRewards,
  onMail,
  onClans,
  showDailyBadge = false,
  mailUnreadCount = 0,
  globalChatSlot = null,
  className,
}) {
  const u = user || {};
  const rawName = (u.username || '').trim();
  const level = Number(u.level) || 1;
  const levelStr = String(level);
  const league = getPlayerLeague(level);
  const avatarUrl = String(u.avatarUrl || '').trim() || GUEST_AVATAR_SRC;
  const [avatarBroken, setAvatarBroken] = useState(false);

  const hubChips = Math.max(0, Math.floor(Number(u.balance ?? 0)));
  const hubBucks = Math.max(
    0,
    Math.floor(Number(u.bucksBalance ?? u.realBalance ?? u.cashBalance ?? 0))
  );

  const normalizedStatus =
    connectionStatus === 'reconnecting' || connectionStatus === 'offline' || connectionStatus === 'online'
      ? connectionStatus
      : null;
  const showSocketStatus = Boolean(normalizedStatus);
  const statusLabel =
    normalizedStatus === 'online'
      ? 'сервер онлайн'
      : normalizedStatus === 'reconnecting'
        ? 'переподключение'
        : 'сервер офлайн';

  return (
    <div className={cn('relative isolate w-full min-w-0 overflow-hidden rounded-b-2xl', className)}>
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
        <img
          src={LOBBY_HOME_ASSETS.decHeaderRail}
          alt=""
          className="absolute -left-[8%] -top-1 w-[116%] max-w-none object-cover object-top opacity-100 sm:-top-2"
          draggable={false}
        />
      </div>

      <div className="relative z-[1] flex w-full min-w-0 flex-col gap-[23px] px-1 pb-2 pt-0 sm:gap-[25px] sm:px-0 sm:pb-2.5">
        <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
          <div
            className="relative flex h-[52px] w-[52px] shrink-0 items-center justify-center sm:h-[57px] sm:w-[57px]"
            aria-label={`Лига: ${league.label}`}
          >
            <img
              src={LOBBY_HOME_ASSETS.iconStarLevel}
              alt=""
              className="pointer-events-none absolute inset-0 h-full w-full object-contain"
              draggable={false}
            />
            <span
              className={cn(
                'relative z-[1] font-black tabular-nums leading-none text-white',
                levelStr.length >= 3 ? 'text-[8px] sm:text-[9px]' : 'text-[9px] sm:text-[10px]'
              )}
              style={strokeTextStyle}
            >
              {level}
            </span>
          </div>

          <button
            type="button"
            onClick={onProfileClick ?? (() => {})}
            aria-label={rawName ? `Профиль: ${rawName}` : 'Профиль'}
            title={rawName || 'Профиль'}
            className="lobby-focus relative isolate h-[52px] w-[52px] shrink-0 overflow-visible sm:h-[57px] sm:w-[57px]"
          >
            {/* Рамка снизу: SVG перекрывает центр — фото только поверх с меньшим кругом */}
            <img
              src={LOBBY_HOME_ASSETS.frameAvatar}
              alt=""
              className="pointer-events-none absolute inset-0 z-0 h-full w-full object-contain"
              draggable={false}
            />
            <img
              src={avatarBroken ? FALLBACK_GUEST_AVATAR_SRC : avatarUrl}
              alt=""
              className="absolute left-1/2 top-1/2 z-[1] h-[70%] w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full object-cover"
              draggable={false}
              onError={() => setAvatarBroken(true)}
            />
          </button>

          <div className="flex min-h-0 min-w-0 flex-1 items-center gap-1.5">
            {rawName ? (
              <span
                className="min-w-0 truncate text-left text-[12px] font-semibold tracking-tight text-white/95 sm:text-sm"
                title={
                  showSocketStatus
                    ? `${rawName} · лига ${league.label} · ${statusLabel}`
                    : `${rawName} · лига ${league.label}`
                }
              >
                {rawName}
              </span>
            ) : (
              <span className="min-w-0 shrink-0 text-left text-[12px] text-white/55 sm:text-sm">Гость</span>
            )}
            {showSocketStatus ? (
              <span
                className={cn(
                  'inline-flex shrink-0 items-center justify-center leading-none',
                  normalizedStatus === 'online' && 'text-emerald-400',
                  normalizedStatus === 'reconnecting' && 'text-cyan-300',
                  normalizedStatus === 'offline' && 'text-red-400'
                )}
                role="status"
                aria-label={
                  normalizedStatus === 'online'
                    ? 'Онлайн: связь с сервером'
                    : normalizedStatus === 'reconnecting'
                      ? 'Переподключение к серверу'
                      : 'Офлайн: нет связи с сервером'
                }
              >
                {normalizedStatus === 'online' ? (
                  <Wifi className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                ) : normalizedStatus === 'reconnecting' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin sm:h-4 sm:w-4" aria-hidden />
                ) : (
                  <WifiOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                )}
              </span>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
            <button
              type="button"
              onClick={onOpenShop ?? (() => {})}
              aria-label={`Магазин, фишки: ${formatHubBalance(hubChips)}`}
              title={`Магазин · ${formatHubBalance(hubChips)} фишек`}
              className={HUB_RESOURCE_BTN}
            >
              <div className={HUB_RESOURCE_RING}>
                <img
                  src={LOBBY_HOME_ASSETS.decKrug1}
                  alt=""
                  className={HUB_RESOURCE_DECOR}
                  draggable={false}
                />
                <img
                  src={LOBBY_HOME_ASSETS.btnCigarettes}
                  alt=""
                  className={HUB_SHOP_BTN_IMG}
                  draggable={false}
                />
              </div>
              <span className={HUB_BALANCE_CAPTION}>{formatHubBalance(hubChips)}</span>
            </button>
            <button
              type="button"
              onClick={onOpenWallet ?? (() => {})}
              aria-label={`Кошелёк и баксы: ${formatHubBalance(hubBucks)}`}
              title={`Баксы · ${formatHubBalance(hubBucks)}`}
              className={HUB_RESOURCE_BTN}
            >
              <div className={HUB_RESOURCE_RING}>
                <img
                  src={LOBBY_HOME_ASSETS.decKrug2}
                  alt=""
                  className={HUB_RESOURCE_DECOR}
                  draggable={false}
                />
                <img
                  src={LOBBY_HOME_ASSETS.btnBucks}
                  alt=""
                  className={HUB_WALLET_BTN_IMG}
                  draggable={false}
                />
              </div>
              <span className={HUB_BALANCE_CAPTION}>{formatHubBalance(hubBucks)}</span>
            </button>
          </div>
        </div>

        <div className="mt-3 flex min-w-0 items-stretch gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={onDailyRewards ?? (() => {})}
            aria-label="Ежедневные ништяки"
            title="Ежедневные ништяки"
            className={HUB_ROW2_BTN}
          >
            <LobbyHubNotificationIndicator count={showDailyBadge ? 1 : 0} layout="absoluteCorner" />
            <img src={LOBBY_HOME_ASSETS.btnCalendar} alt="" className={HUB_ROW2_TILE_IMG} draggable={false} />
          </button>
          <button
            type="button"
            onClick={onMail ?? (() => {})}
            aria-label="Почта"
            title="Почта"
            className={HUB_ROW2_BTN}
          >
            <LobbyHubNotificationIndicator count={mailUnreadCount} layout="absoluteCorner" />
            <img src={LOBBY_HOME_ASSETS.btnMail} alt="" className={HUB_ROW2_TILE_IMG} draggable={false} />
          </button>
          {typeof onClans === 'function' ? (
            <button type="button" onClick={onClans} aria-label="Кланы" title="Кланы" className={HUB_ROW2_BTN}>
              <img src={LOBBY_HOME_ASSETS.btnClans} alt="" className={HUB_ROW2_TILE_IMG} draggable={false} />
            </button>
          ) : null}
          {globalChatSlot ? (
            <div className="relative min-h-[67px] min-w-0 flex-1 sm:min-h-[68px]">
              <img
                src={LOBBY_HOME_ASSETS.frameGlobalChat}
                alt=""
                className="pointer-events-none absolute inset-0 z-0 h-full w-full object-contain object-center"
                draggable={false}
              />
              {/* Внутреннее «окно» под графику рамки: контент не вылезает на ободок */}
              <div className="relative z-[1] flex h-full min-h-[67px] min-w-0 items-stretch pl-2.5 pr-2 pt-[0.35rem] pb-1 sm:min-h-[68px] sm:pl-3 sm:pr-2.5 sm:pt-1 sm:pb-1.5">
                {globalChatSlot}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
